import { Component, computed, inject, input } from '@angular/core';
import { Message } from 'primeng/message';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { ProgressSpinner } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { BrandKit } from '@/app/services/brand-kit.service';

interface PaletteColor {
    name: string;
    hex: string;
    role: string;
}

interface TypographyEntry {
    label: string;
    value: string;
}

/**
 * Read-only preview of a Site Template's brand-kit: the style descriptor as an
 * interactive panel (palette swatches, typography, personality tags) plus the
 * brand-icon references, favicon and OG base plate as images. Host-agnostic —
 * the caller resolves the `BrandKit` (via `BrandKitService`) and supplies the
 * loading flag and an empty-state message; this renders the content only (no
 * card/heading), so it slots into a tab panel or a dialog alike.
 */
@Component({
    selector: 'app-template-info',
    standalone: true,
    imports: [Message, Tag, Tooltip, ProgressSpinner],
    templateUrl: './template-info.html',
})
export class TemplateInfo {
    private readonly messageService = inject(MessageService);

    readonly brandKit = input<BrandKit | null>(null);
    readonly loading = input<boolean>(false);
    readonly emptyMessage = input<string>('No template info available.');

    protected readonly paletteColors = computed<PaletteColor[]>(() => {
        const colors = asRecord(this.brandKit()?.style?.['palette'])?.['colors'];
        if (!Array.isArray(colors)) return [];
        return colors.flatMap((c) => {
            const rec = asRecord(c);
            const hex = asString(rec?.['hex']);
            return hex
                ? [{ name: asString(rec?.['name']) ?? '', hex, role: asString(rec?.['role']) ?? '' }]
                : [];
        });
    });

    protected readonly typographyEntries = computed<TypographyEntry[]>(() => {
        const typo = asRecord(this.brandKit()?.style?.['typography']);
        if (!typo) return [];
        const entries: TypographyEntry[] = [];
        const push = (label: string, key: string): void => {
            const value = asString(typo[key]);
            if (value) entries.push({ label, value });
        };
        push('Display', 'display_font');
        push('Heading', 'heading_font');
        push('Body', 'body_font');
        push('Mono', 'mono_font');
        return entries;
    });

    protected readonly personalityTags = computed<string[]>(() => {
        const tags = this.brandKit()?.style?.['personality_tags'];
        return Array.isArray(tags) ? tags.filter((t): t is string => typeof t === 'string') : [];
    });

    protected readonly styleDescription = computed<string | null>(() =>
        asString(asRecord(this.brandKit()?.style?.['mental_model'])?.['description'])
    );

    protected readonly hasStyleInfo = computed(
        () =>
            this.paletteColors().length > 0 ||
            this.typographyEntries().length > 0 ||
            this.personalityTags().length > 0 ||
            this.styleDescription() !== null
    );

    protected copyHex(hex: string): void {
        void navigator.clipboard?.writeText(hex);
        this.messageService.add({ severity: 'info', summary: 'Copied', detail: hex, life: 1500 });
    }
}

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function asString(value: unknown): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
}
